import { ref, computed, nextTick, reactive, onBeforeUnmount } from 'vue';
import whatsappTemplatesAPI from 'dashboard/api/whatsappTemplates';

const supportedTemplateLanguages = [
  'af',
  'sq',
  'ar',
  'ar_EG',
  'ar_AE',
  'ar_LB',
  'ar_MA',
  'ar_QA',
  'az',
  'be_BY',
  'bn',
  'bn_IN',
  'bg',
  'ca',
  'zh_CN',
  'zh_HK',
  'zh_TW',
  'hr',
  'cs',
  'da',
  'prs_AF',
  'nl',
  'nl_BE',
  'en',
  'en_GB',
  'en_US',
  'en_AE',
  'en_AU',
  'en_CA',
  'en_GH',
  'en_IE',
  'en_IN',
  'en_JM',
  'en_MY',
  'en_NZ',
  'en_QA',
  'en_SG',
  'en_UG',
  'en_ZA',
  'et',
  'fil',
  'fi',
  'fr',
  'fr_BE',
  'fr_CA',
  'fr_CH',
  'fr_CI',
  'fr_MA',
  'ka',
  'de',
  'de_AT',
  'de_CH',
  'el',
  'gu',
  'ha',
  'he',
  'hi',
  'hu',
  'id',
  'ga',
  'it',
  'ja',
  'kn',
  'kk',
  'rw_RW',
  'ko',
  'ky_KG',
  'lo',
  'lv',
  'lt',
  'mk',
  'ms',
  'ml',
  'mr',
  'nb',
  'ps_AF',
  'fa',
  'pl',
  'pt_BR',
  'pt_PT',
  'pa',
  'ro',
  'ru',
  'sr',
  'si_LK',
  'sk',
  'sl',
  'es',
  'es_AR',
  'es_CL',
  'es_CO',
  'es_CR',
  'es_DO',
  'es_EC',
  'es_HN',
  'es_MX',
  'es_PA',
  'es_PE',
  'es_ES',
  'es_UY',
  'sw',
  'sv',
  'ta',
  'te',
  'th',
  'tr',
  'uk',
  'ur',
  'uz',
  'vi',
  'zu',
];

const languageLabels = {
  af: 'Afrikaans',
  sq: 'Albanian',
  ar: 'Arabic',
  ar_EG: 'Arabic (EGY)',
  ar_AE: 'Arabic (UAE)',
  ar_LB: 'Arabic (LBN)',
  ar_MA: 'Arabic (MAR)',
  ar_QA: 'Arabic (QAT)',
  az: 'Azerbaijani',
  be_BY: 'Belarusian',
  bn: 'Bengali',
  bn_IN: 'Bengali (IND)',
  bg: 'Bulgarian',
  ca: 'Catalan',
  zh_CN: 'Chinese (CHN)',
  zh_HK: 'Chinese (HKG)',
  zh_TW: 'Chinese (TAI)',
  hr: 'Croatian',
  cs: 'Czech',
  da: 'Danish',
  prs_AF: 'Dari',
  nl: 'Dutch',
  nl_BE: 'Dutch (BEL)',
  en: 'English',
  en_GB: 'English (UK)',
  en_US: 'English (US)',
  en_AE: 'English (UAE)',
  en_AU: 'English (AUS)',
  en_CA: 'English (CAN)',
  en_GH: 'English (GHA)',
  en_IE: 'English (IRL)',
  en_IN: 'English (IND)',
  en_JM: 'English (JAM)',
  en_MY: 'English (MYS)',
  en_NZ: 'English (NZL)',
  en_QA: 'English (QAT)',
  en_SG: 'English (SGP)',
  en_UG: 'English (UGA)',
  en_ZA: 'English (ZAF)',
  et: 'Estonian',
  fil: 'Filipino',
  fi: 'Finnish',
  fr: 'French',
  fr_BE: 'French (BEL)',
  fr_CA: 'French (CAN)',
  fr_CH: 'French (CHE)',
  fr_CI: 'French (CIV)',
  fr_MA: 'French (MAR)',
  ka: 'Georgian',
  de: 'German',
  de_AT: 'German (AUT)',
  de_CH: 'German (CHE)',
  el: 'Greek',
  gu: 'Gujarati',
  ha: 'Hausa',
  he: 'Hebrew',
  hi: 'Hindi',
  hu: 'Hungarian',
  id: 'Indonesian',
  ga: 'Irish',
  it: 'Italian',
  ja: 'Japanese',
  kn: 'Kannada',
  kk: 'Kazakh',
  rw_RW: 'Kinyarwanda',
  ko: 'Korean',
  ky_KG: 'Kyrgyz (Kyrgyzstan)',
  lo: 'Lao',
  lv: 'Latvian',
  lt: 'Lithuanian',
  mk: 'Macedonian',
  ms: 'Malay',
  ml: 'Malayalam',
  mr: 'Marathi',
  nb: 'Norwegian',
  ps_AF: 'Pashto',
  fa: 'Persian',
  pl: 'Polish',
  pt_BR: 'Portuguese (BR)',
  pt_PT: 'Portuguese (POR)',
  pa: 'Punjabi',
  ro: 'Romanian',
  ru: 'Russian',
  sr: 'Serbian',
  si_LK: 'Sinhala',
  sk: 'Slovak',
  sl: 'Slovenian',
  es: 'Spanish',
  es_AR: 'Spanish (ARG)',
  es_CL: 'Spanish (CHL)',
  es_CO: 'Spanish (COL)',
  es_CR: 'Spanish (CRI)',
  es_DO: 'Spanish (DOM)',
  es_EC: 'Spanish (ECU)',
  es_HN: 'Spanish (HND)',
  es_MX: 'Spanish (MEX)',
  es_PA: 'Spanish (PAN)',
  es_PE: 'Spanish (PER)',
  es_ES: 'Spanish (SPA)',
  es_UY: 'Spanish (URY)',
  sw: 'Swahili',
  sv: 'Swedish',
  ta: 'Tamil',
  te: 'Telugu',
  th: 'Thai',
  tr: 'Turkish',
  uk: 'Ukrainian',
  ur: 'Urdu',
  uz: 'Uzbek',
  vi: 'Vietnamese',
  zu: 'Zulu',
};

export function useTemplateForm() {
  const name = ref('');
  const category = ref('UTILITY');
  const language = ref('en_US');
  const parameterFormat = ref('positional'); // 'positional' | 'named'
  const headerType = ref('NONE');
  const headerText = ref('');
  const headerMediaFile = ref(null);
  const headerMediaPreviewUrl = ref('');
  const headerExistingHandle = ref(null); // synced header_handle from Meta — reused on edit when no new file
  const bodyText = ref('');
  const footerText = ref('');
  const quickReplies = ref([]);
  const ctaButtons = ref([]);
  const sampleVariables = ref({});
  const variableTypes = ref({});
  const isSubmitting = ref(false);
  const fieldErrors = reactive({});
  const metaTemplateId = ref(null);
  const templateStatus = ref(null);
  const originalSnapshot = ref(null);

  // AUTHENTICATION template fields (COPY_CODE MVP)
  const authSecurityRecommendation = ref(true);
  const authCodeExpirationMinutes = ref('');
  const authButtonText = ref('');

  // Matches both {{1}} (positional) and {{first_name}} (named)
  const VAR_REGEX = /\{\{([a-zA-Z_][a-zA-Z0-9_]*|\d+)\}\}/g;

  const extractVars = text =>
    Array.from(new Set((text.match(VAR_REGEX) || []).map(m => m.slice(2, -2))));

  const variablesInBody = computed(() => extractVars(bodyText.value));
  const variablesInHeader = computed(() => extractVars(headerText.value));

  const canAddQuickReply = computed(() => quickReplies.value.length < 3);
  const canAddCta = computed(() => ctaButtons.value.length < 2);
  // Meta allows at most one variable in a TEXT header (named or positional).
  const canInsertHeaderVariable = computed(
    () => variablesInHeader.value.length < 1
  );
  const headerHasText = computed(() => headerType.value === 'TEXT');
  const headerHasMedia = computed(() =>
    ['IMAGE', 'DOCUMENT', 'VIDEO'].includes(headerType.value)
  );
  const headerFileName = computed(() => headerMediaFile.value?.name || '');
  const headerAcceptAttr = computed(() => {
    if (headerType.value === 'IMAGE') return 'image/*';
    if (headerType.value === 'VIDEO') return 'video/*';
    if (headerType.value === 'DOCUMENT') return 'application/*,application/pdf';
    return '*/*';
  });

  const getAllVariables = computed(() => {
    const varSet = new Set();
    const add = text => extractVars(text).forEach(v => varSet.add(v));
    if (headerType.value === 'TEXT' && headerText.value) add(headerText.value);
    if (bodyText.value) add(bodyText.value);
    if (footerText.value) add(footerText.value);

    const vars = Array.from(varSet);
    // Positional: sort numerically; Named: sort alphabetically
    if (parameterFormat.value === 'positional') {
      return vars.sort((a, b) => parseInt(a, 10) - parseInt(b, 10));
    }
    return vars.sort();
  });

  const replaceVariables = text => {
    if (!text) return '';
    return text.replace(
      VAR_REGEX,
      (_, key) => sampleVariables.value[key] || `[${key}]`
    );
  };

  const getPreviewBody = computed(() => replaceVariables(bodyText.value) || '');
  const getPreviewFooter = computed(
    () => replaceVariables(footerText.value) || ''
  );
  const getPreviewHeader = computed(() =>
    headerType.value === 'TEXT' ? replaceVariables(headerText.value) || '' : ''
  );

  const getPreviewQuickReplies = computed(() =>
    quickReplies.value.map(qr => qr || '')
  );
  const getPreviewCtaButtons = computed(() =>
    ctaButtons.value.map(btn => ({
      type: btn.type,
      label: btn.label || '',
      value: btn.value || '',
    }))
  );

  const insertVariableAtCursor = el => {
    if (!el) return;
    const pos = el.selectionStart ?? el.value.length;
    const token =
      parameterFormat.value === 'named'
        ? `{{variable_name}}`
        : `{{${(bodyText.value.match(/\{\{\d+\}\}/g) || []).length + 1}}}`;
    bodyText.value =
      bodyText.value.slice(0, pos) + token + bodyText.value.slice(pos);
    nextTick(() => {
      el.focus();
      el.setSelectionRange(pos + token.length, pos + token.length);
    });
  };

  const insertHeaderVariable = el => {
    if (!el || !canInsertHeaderVariable.value) return;
    const pos = el.selectionStart ?? el.value.length;
    const token =
      parameterFormat.value === 'named'
        ? `{{variable_name}}`
        : `{{${(headerText.value.match(/\{\{\d+\}\}/g) || []).length + 1}}}`;
    headerText.value =
      headerText.value.slice(0, pos) + token + headerText.value.slice(pos);
    nextTick(() => {
      el.focus();
      el.setSelectionRange(pos + token.length, pos + token.length);
    });
  };

  const addQuickReply = () => {
    if (canAddQuickReply.value) quickReplies.value.push('');
  };
  const removeQuickReply = i => quickReplies.value.splice(i, 1);
  const addCta = () => {
    if (canAddCta.value)
      ctaButtons.value.push({ type: 'URL', label: '', value: '' });
  };
  const removeCta = i => ctaButtons.value.splice(i, 1);

  const onHeaderFileChange = event => {
    const file = event.target?.files?.[0];
    if (headerMediaPreviewUrl.value)
      URL.revokeObjectURL(headerMediaPreviewUrl.value);
    headerMediaFile.value = file || null;
    headerMediaPreviewUrl.value = file ? URL.createObjectURL(file) : '';
  };

  const clearHeaderFile = () => {
    if (headerMediaPreviewUrl.value)
      URL.revokeObjectURL(headerMediaPreviewUrl.value);
    headerMediaFile.value = null;
    headerMediaPreviewUrl.value = '';
    headerExistingHandle.value = null;
  };

  onBeforeUnmount(clearHeaderFile);

  const resetForm = () => {
    name.value = '';
    category.value = 'UTILITY';
    language.value = 'en_US';
    parameterFormat.value = 'positional';
    headerType.value = 'NONE';
    headerText.value = '';
    clearHeaderFile();
    bodyText.value = '';
    footerText.value = '';
    quickReplies.value = [];
    ctaButtons.value = [];
    sampleVariables.value = {};
    variableTypes.value = {};
    authSecurityRecommendation.value = true;
    authCodeExpirationMinutes.value = '';
    authButtonText.value = '';
    Object.keys(fieldErrors).forEach(k => delete fieldErrors[k]);
  };

  const validate = t => {
    Object.keys(fieldErrors).forEach(k => delete fieldErrors[k]);
    let valid = true;

    if (!name.value) {
      fieldErrors.name = t('INBOX_MGMT.TEMPLATES.CREATE.VALIDATION_REQUIRED');
      valid = false;
    } else if (!/^[a-z0-9_]+$/.test(name.value)) {
      fieldErrors.name = t('INBOX_MGMT.TEMPLATES.CREATE.NAME_VALIDATION_ERROR');
      valid = false;
    }

    // AUTHENTICATION templates have a fixed preset body — skip free-form body/button checks.
    if (category.value === 'AUTHENTICATION') {
      const rawExpiry = authCodeExpirationMinutes.value?.toString().trim();
      if (rawExpiry) {
        const mins = Number(rawExpiry);
        if (!Number.isInteger(mins) || mins < 1 || mins > 90) {
          fieldErrors.authExpiry = t(
            'INBOX_MGMT.TEMPLATES.CREATE.AUTH_CODE_EXPIRY_ERROR'
          );
          valid = false;
        }
      }
      return valid;
    }

    if (!bodyText.value) {
      fieldErrors.body = t('INBOX_MGMT.TEMPLATES.CREATE.VALIDATION_REQUIRED');
      valid = false;
    } else {
      const matches = bodyText.value.match(/\{\{([^}]+)\}\}/g) || [];
      if (matches.length > 10) {
        fieldErrors.body = t('INBOX_MGMT.TEMPLATES.CREATE.MAX_VARIABLES_ERROR');
        valid = false;
      }

      if (parameterFormat.value === 'positional') {
        if (matches.length > 0) {
          const nums = matches.map(m => parseInt(m.slice(2, -2), 10));
          const sorted = [...nums].sort((a, b) => a - b);
          const expected = Array.from(
            { length: sorted.length },
            (_, i) => i + 1
          );
          if (sorted.join() !== expected.join()) {
            fieldErrors.body = t(
              'INBOX_MGMT.TEMPLATES.CREATE.PLACEHOLDER_ERROR'
            );
            valid = false;
          }
        }
      } else {
        const badVar = matches.find(
          m => !/^[a-z_][a-z0-9_]*$/.test(m.slice(2, -2))
        );
        if (badVar) {
          fieldErrors.body = t(
            'INBOX_MGMT.TEMPLATES.CREATE.NAMED_VARIABLE_ERROR',
            { name: badVar.slice(2, -2) }
          );
          valid = false;
        }
      }
    }

    const emptyQR = quickReplies.value.findIndex(qr => !qr?.trim());
    if (emptyQR !== -1) {
      fieldErrors.buttons = t(
        'INBOX_MGMT.TEMPLATES.CREATE.QUICK_REPLY_LABEL_REQUIRED',
        { n: emptyQR + 1 }
      );
      valid = false;
    }

    if (valid) {
      const emptyLabel = ctaButtons.value.findIndex(btn => !btn.label?.trim());
      if (emptyLabel !== -1) {
        fieldErrors.buttons = t(
          'INBOX_MGMT.TEMPLATES.CREATE.CTA_LABEL_REQUIRED',
          { n: emptyLabel + 1 }
        );
        valid = false;
      }
    }

    if (valid) {
      const emptyValue = ctaButtons.value.findIndex(btn => !btn.value?.trim());
      if (emptyValue !== -1) {
        const btn = ctaButtons.value[emptyValue];
        fieldErrors.buttons = t(
          'INBOX_MGMT.TEMPLATES.CREATE.CTA_VALUE_REQUIRED',
          {
            n: emptyValue + 1,
            field: btn.type === 'URL' ? 'URL' : 'phone number',
          }
        );
        valid = false;
      }
    }

    return valid;
  };

  const buildPayload = async () => {
    // AUTHENTICATION (COPY_CODE) uses a Meta-fixed preset body; emit the OTP component shape.
    if (category.value === 'AUTHENTICATION') {
      const components = [
        {
          type: 'BODY',
          add_security_recommendation: authSecurityRecommendation.value,
        },
      ];
      const rawExpiry = authCodeExpirationMinutes.value?.toString().trim();
      if (rawExpiry) {
        const expiryMinutes = Number(rawExpiry);
        if (Number.isInteger(expiryMinutes) && expiryMinutes >= 1 && expiryMinutes <= 90) {
          components.push({
            type: 'FOOTER',
            code_expiration_minutes: expiryMinutes,
          });
        }
      }
      const btnLabel = authButtonText.value?.trim();
      components.push({
        type: 'BUTTONS',
        buttons: [
          {
            type: 'OTP',
            otp_type: 'COPY_CODE',
            ...(btnLabel ? { text: btnLabel } : {}),
          },
        ],
      });
      return {
        name: name.value,
        category: category.value,
        language: language.value,
        components,
      };
    }

    let mediaContent = null;
    if (headerMediaFile.value) {
      mediaContent = await new Promise(resolve => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result.split(',')[1]);
        reader.readAsDataURL(headerMediaFile.value);
      });
    }

    // Backend (TemplateCreationService) expects the develop-era shape:
    // - BODY.examples: { [varKey]: sampleValue }
    // - HEADER.example: string sample (for TEXT headers with variables)
    // It transforms these into Meta's example.body_text / body_text_named_params.
    const bodyExamples = bodyText.value?.includes('{{')
      ? { ...sampleVariables.value }
      : undefined;

    const headerExample =
      headerType.value === 'TEXT' && headerText.value?.includes('{{')
        ? sampleVariables.value[extractVars(headerText.value)[0]] ||
          'Example Header'
        : undefined;

    return {
      name: name.value,
      category: category.value,
      language: language.value,
      parameter_format: parameterFormat.value,
      components: [
        ...(headerType.value !== 'NONE'
          ? [
              {
                type: 'HEADER',
                format: headerType.value,
                text:
                  headerType.value === 'TEXT' ? headerText.value : undefined,
                example: headerExample,
                // For media headers: pass a new upload OR the existing handle
                media: headerMediaFile.value
                  ? {
                      name: headerMediaFile.value.name,
                      size: headerMediaFile.value.size,
                      type: headerMediaFile.value.type,
                      content: mediaContent,
                    }
                  : undefined,
                // Reuse the synced handle when no new file is provided (edit mode)
                existingHandle:
                  !headerMediaFile.value &&
                  ['IMAGE', 'VIDEO', 'DOCUMENT'].includes(headerType.value) &&
                  headerExistingHandle.value
                    ? headerExistingHandle.value
                    : undefined,
              },
            ]
          : []),
        {
          type: 'BODY',
          text: bodyText.value,
          examples: bodyExamples,
        },
        ...(footerText.value
          ? [{ type: 'FOOTER', text: footerText.value }]
          : []),
        ...(quickReplies.value.length || ctaButtons.value.length
          ? [
              {
                type: 'BUTTONS',
                buttons: [
                  ...quickReplies.value.map(label => ({
                    type: 'QUICK_REPLY',
                    text: label,
                  })),
                  ...ctaButtons.value
                    .map(btn => {
                      if (btn.type === 'URL')
                        return {
                          type: 'URL',
                          text: btn.label,
                          url: btn.value,
                          example: btn.value?.includes('{{')
                            ? 'example'
                            : undefined,
                        };
                      if (btn.type === 'PHONE_NUMBER')
                        return {
                          type: 'PHONE_NUMBER',
                          text: btn.label,
                          phone_number: btn.value,
                        };
                      return null;
                    })
                    .filter(Boolean),
                ],
              },
            ]
          : []),
      ],
    };
  };

  const onCreate = async (inboxId, accountId, t, alertFn, emitFn) => {
    if (isSubmitting.value) return;
    if (!validate(t)) {
      alertFn(Object.values(fieldErrors)[0]);
      return;
    }

    try {
      isSubmitting.value = true;
      const payload = await buildPayload();
      const response = await whatsappTemplatesAPI.create(accountId, {
        inboxId,
        template: payload,
      });

      if (response.status === 201) {
        alertFn(t('INBOX_MGMT.TEMPLATES.CREATE.SUCCESS_MESSAGE'));
        try {
          const syncResp = await whatsappTemplatesAPI.list(accountId, inboxId);
          emitFn('synced', syncResp.data?.templates || []);
        } catch (_) {
          // sync failure is non-blocking
        }
        emitFn('close');
        resetForm();
      } else {
        alertFn(
          response.data?.error ||
            response.data?.details ||
            t('INBOX_MGMT.TEMPLATES.CREATE.ERROR_MESSAGE')
        );
      }
    } catch (error) {
      const errorData = error.response?.data;
      let errorMessage = t('INBOX_MGMT.TEMPLATES.CREATE.ERROR_MESSAGE');
      if (errorData?.error) {
        if (errorData.error.includes('Not allowed nest placeholder'))
          errorMessage = t('INBOX_MGMT.TEMPLATES.CREATE.PLACEHOLDER_ERROR');
        else if (errorData.error.includes('App ID not found'))
          errorMessage = t('INBOX_MGMT.TEMPLATES.CREATE.APP_ID_ERROR');
        else errorMessage = errorData.error;
      } else if (errorData?.details) {
        errorMessage = errorData.details;
      }
      alertFn(errorMessage);
    } finally {
      isSubmitting.value = false;
    }
  };

  // Dirty-check helpers
  const isContentDirty = () => {
    const snap = originalSnapshot.value;
    if (!snap) return false;
    return (
      snap.headerType !== headerType.value ||
      snap.headerText !== headerText.value ||
      snap.bodyText !== bodyText.value ||
      snap.footerText !== footerText.value ||
      JSON.stringify(snap.quickReplies) !==
        JSON.stringify(quickReplies.value) ||
      JSON.stringify(snap.ctaButtons) !== JSON.stringify(ctaButtons.value) ||
      // New upload, or cleared/replaced retained Meta header_handle
      headerMediaFile.value !== null ||
      snap.headerExistingHandle !== headerExistingHandle.value ||
      // Auth fields
      snap.authSecurityRecommendation !== authSecurityRecommendation.value ||
      snap.authCodeExpirationMinutes !== authCodeExpirationMinutes.value ||
      snap.authButtonText !== authButtonText.value
    );
  };

  const isCategoryDirty = () => {
    const snap = originalSnapshot.value;
    if (!snap) return false;
    return snap.category !== category.value;
  };

  const syncAfterUpdate = async (accountId, inboxId, emitFn) => {
    try {
      const syncResp = await whatsappTemplatesAPI.list(accountId, inboxId);
      emitFn('synced', syncResp.data?.templates || []);
    } catch (_) {
      // sync failure is non-blocking
    }
  };

  const markCategorySynced = () => {
    if (!originalSnapshot.value) return;
    originalSnapshot.value = {
      ...originalSnapshot.value,
      category: category.value,
    };
  };

  const onUpdate = async (inboxId, accountId, t, alertFn, emitFn) => {
    if (isSubmitting.value) return;

    const categoryChanged = isCategoryDirty();
    const contentChanged = isContentDirty();

    if (!categoryChanged && !contentChanged) {
      alertFn(t('INBOX_MGMT.TEMPLATES.EDIT.NO_CHANGES'));
      return;
    }

    if (contentChanged && !validate(t)) {
      alertFn(Object.values(fieldErrors)[0]);
      return;
    }

    isSubmitting.value = true;
    let categoryUpdated = false;
    try {
      // Prepare content payload before any Meta mutations so media-prep
      // failures cannot leave a committed category change behind.
      let payload = null;
      if (contentChanged) {
        payload = await buildPayload();
      }

      if (categoryChanged) {
        const catResp = await whatsappTemplatesAPI.updateTemplateCategory(
          accountId,
          metaTemplateId.value,
          { inboxId, category: category.value }
        );
        if (!catResp.data?.message) {
          alertFn(
            catResp.data?.error ||
              t('INBOX_MGMT.TEMPLATES.EDIT.CATEGORY_ERROR_MESSAGE')
          );
          return;
        }
        categoryUpdated = true;
        markCategorySynced();
      }

      if (contentChanged) {
        const compResp = await whatsappTemplatesAPI.updateTemplate(
          accountId,
          metaTemplateId.value,
          {
            inboxId,
            template: {
              components: payload.components,
              parameter_format: payload.parameter_format,
            },
          }
        );
        if (!compResp.data?.message) {
          if (categoryUpdated) {
            await syncAfterUpdate(accountId, inboxId, emitFn);
            alertFn(
              compResp.data?.error ||
                t('INBOX_MGMT.TEMPLATES.EDIT.PARTIAL_CATEGORY_SAVED')
            );
          } else {
            alertFn(
              compResp.data?.error ||
                t('INBOX_MGMT.TEMPLATES.EDIT.ERROR_MESSAGE')
            );
          }
          return;
        }
      }

      alertFn(t('INBOX_MGMT.TEMPLATES.EDIT.SUCCESS_MESSAGE'));
      await syncAfterUpdate(accountId, inboxId, emitFn);
      emitFn('close');
    } catch (error) {
      const errorData = error.response?.data;
      if (categoryUpdated) {
        await syncAfterUpdate(accountId, inboxId, emitFn);
        alertFn(
          errorData?.error ||
            errorData?.details ||
            t('INBOX_MGMT.TEMPLATES.EDIT.PARTIAL_CATEGORY_SAVED')
        );
      } else {
        alertFn(
          errorData?.error ||
            errorData?.details ||
            t('INBOX_MGMT.TEMPLATES.EDIT.ERROR_MESSAGE')
        );
      }
    } finally {
      isSubmitting.value = false;
    }
  };

  // Populate form state from a synced Meta template object.
  // Stores an originalSnapshot for dirty-checking in onUpdate.
  const loadFromTemplate = template => {
    if (!template) return;

    metaTemplateId.value = template.id ?? null;
    templateStatus.value = template.status ?? null;

    name.value = template.name ?? '';
    category.value = template.category ?? 'UTILITY';
    language.value = template.language ?? 'en_US';
    parameterFormat.value =
      template.parameter_format?.toLowerCase() === 'named'
        ? 'named'
        : 'positional';

    // Reset component fields
    headerType.value = 'NONE';
    headerText.value = '';
    clearHeaderFile();
    headerExistingHandle.value = null;
    bodyText.value = '';
    footerText.value = '';
    quickReplies.value = [];
    ctaButtons.value = [];
    sampleVariables.value = {};
    variableTypes.value = {};
    authSecurityRecommendation.value = true;
    authCodeExpirationMinutes.value = '';
    authButtonText.value = '';

    (template.components || []).forEach(component => {
      const type = component.type?.toUpperCase();
      if (type === 'HEADER') {
        const fmt = (component.format || 'TEXT').toUpperCase();
        headerType.value = fmt;
        if (fmt === 'TEXT') {
          headerText.value = component.text ?? '';
          const namedParams = component.example?.header_text_named_params || [];
          namedParams.forEach(({ param_name: pn, example: ex }) => {
            sampleVariables.value[pn] = ex ?? '';
          });
          // Meta header_text is a flat sample array (unlike body_text's nested rows).
          (component.example?.header_text || []).forEach((val, idx) => {
            sampleVariables.value[String(idx + 1)] = val ?? '';
          });
        } else {
          // Save the existing header_handle so the backend can reuse it
          // when user edits content without uploading a new file.
          const handle = component.example?.header_handle?.[0] ?? null;
          headerExistingHandle.value = handle;
        }
      } else if (type === 'BODY') {
        if (template.category === 'AUTHENTICATION') {
          authSecurityRecommendation.value =
            component.add_security_recommendation !== false;
        } else {
          bodyText.value = component.text ?? '';
          const namedParams = component.example?.body_text_named_params || [];
          namedParams.forEach(({ param_name: pn, example: ex }) => {
            sampleVariables.value[pn] = ex ?? '';
          });
          const positionalSamples =
            (component.example?.body_text || [])[0] || [];
          positionalSamples.forEach((val, idx) => {
            sampleVariables.value[String(idx + 1)] = val ?? '';
          });
        }
      } else if (type === 'FOOTER') {
        if (template.category === 'AUTHENTICATION') {
          authCodeExpirationMinutes.value = component.code_expiration_minutes
            ? String(component.code_expiration_minutes)
            : '';
        } else {
          footerText.value = component.text ?? '';
        }
      } else if (type === 'BUTTONS') {
        (component.buttons || []).forEach(btn => {
          const btnType = btn.type?.toUpperCase();
          if (template.category === 'AUTHENTICATION') {
            // Meta stores OTP button as URL after approval; reconstruct auth button label.
            if (btnType === 'URL' || btnType === 'OTP') {
              authButtonText.value = btn.text ?? '';
            }
          } else if (btnType === 'QUICK_REPLY') {
            quickReplies.value.push(btn.text ?? '');
          } else if (btnType === 'URL') {
            ctaButtons.value.push({
              type: 'URL',
              label: btn.text ?? '',
              value: btn.url ?? '',
            });
          } else if (btnType === 'PHONE_NUMBER') {
            ctaButtons.value.push({
              type: 'PHONE_NUMBER',
              label: btn.text ?? '',
              value: btn.phone_number ?? '',
            });
          }
        });
      }
    });

    // Snapshot state for dirty-checking
    originalSnapshot.value = {
      category: category.value,
      headerType: headerType.value,
      headerText: headerText.value,
      bodyText: bodyText.value,
      footerText: footerText.value,
      quickReplies: JSON.parse(JSON.stringify(quickReplies.value)),
      ctaButtons: JSON.parse(JSON.stringify(ctaButtons.value)),
      headerExistingHandle: headerExistingHandle.value,
      authSecurityRecommendation: authSecurityRecommendation.value,
      authCodeExpirationMinutes: authCodeExpirationMinutes.value,
      authButtonText: authButtonText.value,
    };

    Object.keys(fieldErrors).forEach(k => delete fieldErrors[k]);
  };

  return {
    name,
    category,
    language,
    parameterFormat,
    headerType,
    headerText,
    headerMediaFile,
    headerMediaPreviewUrl,
    headerExistingHandle,
    bodyText,
    footerText,
    quickReplies,
    ctaButtons,
    sampleVariables,
    variableTypes,
    isSubmitting,
    fieldErrors,
    variablesInBody,
    variablesInHeader,
    canAddQuickReply,
    canAddCta,
    canInsertHeaderVariable,
    headerHasText,
    headerHasMedia,
    headerFileName,
    headerAcceptAttr,
    getAllVariables,
    getPreviewBody,
    getPreviewHeader,
    getPreviewFooter,
    getPreviewQuickReplies,
    getPreviewCtaButtons,
    languageLabels,
    supportedTemplateLanguages,
    insertVariableAtCursor,
    insertHeaderVariable,
    addQuickReply,
    removeQuickReply,
    addCta,
    removeCta,
    onHeaderFileChange,
    clearHeaderFile,
    resetForm,
    validate,
    buildPayload,
    onCreate,
    replaceVariables,
    originalSnapshot,
    metaTemplateId,
    templateStatus,
    loadFromTemplate,
    onUpdate,
    authSecurityRecommendation,
    authCodeExpirationMinutes,
    authButtonText,
  };
}
